import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  Container
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      isChunkLoadError: false
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a chunk loading error (common after deployments)
    const isChunkError = error && (
      error.name === 'ChunkLoadError' ||
      (error.message && error.message.includes('Loading chunk')) ||
      (error.message && error.message.includes('Failed to fetch dynamically imported module'))
    );

    // Update state so the next render will show the fallback UI
    return { hasError: true, isChunkLoadError: isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }

    // Check for chunk load error
    const isChunkError = error && (
      error.name === 'ChunkLoadError' ||
      (error.message && error.message.includes('Loading chunk')) ||
      (error.message && error.message.includes('Failed to fetch dynamically imported module'))
    );

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
      isChunkLoadError: isChunkError
    }));

    // Auto-reload for chunk load errors (the app was updated)
    if (isChunkError) {
      console.log('Chunk load error detected - app may have been updated. Reload recommended.');
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkLoadError: false
    });

    // If errors keep occurring, refresh the page
    if (this.state.errorCount > 3) {
      window.location.reload();
    }
  };

  handleHardReload = () => {
    // Clear cache and do a hard reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    // Force reload from server, not cache
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                maxWidth: 500
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: 64,
                  color: this.state.isChunkLoadError ? 'info.main' : 'error.main',
                  mb: 2
                }}
              />

              <Typography variant="h5" gutterBottom color={this.state.isChunkLoadError ? 'info.main' : 'error'}>
                {this.state.isChunkLoadError
                  ? 'App Update Available'
                  : 'Oops! Something went wrong'}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ mb: 3 }}
              >
                {this.state.isChunkLoadError
                  ? 'The application has been updated. Please reload the page to get the latest version.'
                  : 'We encountered an unexpected error. The issue has been logged and our team will investigate it.'}
              </Typography>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 3, 
                    bgcolor: 'grey.50',
                    textAlign: 'left'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    component="pre"
                    sx={{ 
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      overflow: 'auto'
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo && (
                      <>
                        {'\n\nComponent Stack:'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </Typography>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                {this.state.isChunkLoadError ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleHardReload}
                    size="large"
                  >
                    Reload Page
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<RefreshIcon />}
                      onClick={this.handleReset}
                      size="large"
                    >
                      Try Again
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => window.location.href = '/'}
                      size="large"
                    >
                      Go Home
                    </Button>
                  </>
                )}
              </Box>

              {this.state.errorCount > 2 && !this.state.isChunkLoadError && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ mt: 2, display: 'block' }}
                >
                  Multiple errors detected. Consider refreshing the page.
                </Typography>
              )}
            </Paper>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;